import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { feeStatusLabel, feeStatusColor, formatMoney } from "../../lib/feeStatus";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { DateFilterPicker } from "./DateFilterPicker";
import { PromotionsPanel } from "./PromotionsPanel";
import { formatDate } from "../../lib/dateUtils";

const STATUS_OPTIONS = [
  "awaiting_payment", "verification_pending", "verified", "payment_not_found",
  "incorrect_amount", "partially_paid", "waived", "refunded", "cancelled", "not_required",
];

const SummaryCard = ({ label, value, testId }) => (
  <div className="border border-soft-gray rounded-2xl p-5" data-testid={testId}>
    <p className="text-2xl font-extrabold">{value}</p>
    <p className="text-xs font-bold uppercase tracking-wide text-midnight-black/50 mt-1">{label}</p>
  </div>
);

const FeesList = () => {
  const navigate = useNavigate();
  const [fees, setFees] = useState(null);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("");
  const [partyType, setPartyType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = () => {
    api.get("/admin/booking-fees", { params: { status: status || undefined, party_type: partyType || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined } }).then(({ data }) => setFees(data));
    api.get("/admin/booking-fees/summary").then(({ data }) => setSummary(data));
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, partyType, dateFrom, dateTo]);

  return (
    <div>
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="fee-summary-cards">
          <SummaryCard label="Awaiting Stranger Payment" value={summary.awaiting_stranger_payment} testId="summary-awaiting-stranger" />
          <SummaryCard label="Awaiting Verification" value={summary.awaiting_verification} testId="summary-awaiting-verification" />
          <SummaryCard label="Verified Today" value={summary.verified_today} testId="summary-verified-today" />
          <SummaryCard label="Photographer Fees Outstanding" value={summary.photographer_fees_outstanding} testId="summary-photographer-outstanding" />
          <SummaryCard label="Photographer Fees Verified" value={summary.photographer_fees_verified} testId="summary-photographer-verified" />
          <SummaryCard label="Discounted Fees" value={summary.discounted_fees} testId="summary-discounted" />
          <SummaryCard label="Waived / Comp Fees" value={summary.waived_fees} testId="summary-waived" />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="w-44">
          <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Status</label>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="fee-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{feeStatusLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Party</label>
          <Select value={partyType || "all"} onValueChange={(v) => setPartyType(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="fee-party-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Strangers &amp; Photographers</SelectItem>
              <SelectItem value="stranger">Strangers</SelectItem>
              <SelectItem value="photographer">Photographers</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DateFilterPicker label="From" testId="fee-date-from-input" value={dateFrom} onChange={setDateFrom} />
        <DateFilterPicker label="To" testId="fee-date-to-input" value={dateTo} onChange={setDateTo} />
      </div>

      {fees === null ? (
        <p className="font-medium text-midnight-black/60">Loading booking fees...</p>
      ) : fees.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-booking-fees">No booking fees match.</p>
      ) : (
        <div className="space-y-3" data-testid="booking-fees-list">
          {fees.map((f) => (
            <div
              key={f.id}
              data-testid={`fee-row-${f.id}`}
              onClick={() => navigate(`/admin/payments/${f.id}`)}
              className="cursor-pointer border border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap hover:border-stranger-orange transition-colors"
            >
              <div>
                <p className="font-extrabold text-sm uppercase">{f.booking_number || "—"} · {f.party_type === "stranger" ? f.stranger_name : f.photographer_name}</p>
                <p className="text-xs font-medium text-midnight-black/60">{f.session_title} · {formatDate(f.session_date)}</p>
                <p className="text-[10px] font-bold uppercase tracking-wide text-midnight-black/40 mt-1">{f.party_type} fee · {f.fee_type}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-extrabold text-sm">{formatMoney(f.final_amount)}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full whitespace-nowrap ${feeStatusColor(f.status)}`} data-testid={`fee-status-${f.id}`}>
                  {feeStatusLabel(f.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const BookingFeesTab = () => (
  <div data-testid="admin-payments-tab">
    <Tabs defaultValue="fees">
      <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-8" data-testid="payments-sub-tabs">
        <TabsTrigger value="fees" data-testid="payments-subtab-fees" className="text-xs font-bold uppercase tracking-wide px-4 py-2 rounded-full border-2 border-soft-gray data-[state=active]:bg-stranger-orange data-[state=active]:text-clean-white data-[state=active]:border-stranger-orange">
          Booking Fees
        </TabsTrigger>
        <TabsTrigger value="promotions" data-testid="payments-subtab-promotions" className="text-xs font-bold uppercase tracking-wide px-4 py-2 rounded-full border-2 border-soft-gray data-[state=active]:bg-stranger-orange data-[state=active]:text-clean-white data-[state=active]:border-stranger-orange">
          Promotions
        </TabsTrigger>
      </TabsList>
      <TabsContent value="fees" data-testid="payments-subtab-content-fees">
        <FeesList />
      </TabsContent>
      <TabsContent value="promotions" data-testid="payments-subtab-content-promotions">
        <PromotionsPanel />
      </TabsContent>
    </Tabs>
  </div>
);
